<script>
import RepairFileService from '@/services/RepairFileService'
import TransportModalityConstant from '@/constants/transportModalityConstant'
import { handleResponseError } from '@/components/utils/globalFunction'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'
import router from '@/router'

export default {
  name: 'CurrentRepairFiles',
  data() {
    return {
      loading: false,
      sort: 'business_created_day_count',
      order: 'asc',
      isOrderNeutral: true,
      page: 1,
      maxPage: null,
      limit: 20,
      loadingMore: false,
      headers: [
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.REPAIR_FILE_BOOMERANG_ID'),
          value: 'internal_command_number'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.COMPANY_NAME'),
          value: 'company'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.MANUFACTURER_NAME'),
          value: 'manufacturer_name'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.PRODUCT_NAME'),
          value: 'product_name'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.STATUS'),
          value: 'status_macro'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.TIME'),
          value: 'business_created_day_count',
          sortable: true,
          width: '120px'
        },
        {
          title: this.$t('CURRENT_REPAIR_FILES.TABLE.MODALITY'),
          value: 'transporter_modality'
        }
      ],
      repairFileList: []
    }
  },
  async created() {
    await this.getRepairFileList()
  },
  mounted() {
    this.setupInfiniteScroll()
  },
  beforeUnmount() {
    this.cleanupInfiniteScroll()
  },
  watch: {
    repairFileList() {
      this.cleanupInfiniteScroll()
      this.setupInfiniteScroll()
    }
  },
  computed: {
    ascSortIcon() {
      return this.isOrderNeutral ? 'mdi-chevron-right' : 'mdi-chevron-up'
    },
    descSortIcon() {
      return this.isOrderNeutral ? 'mdi-chevron-right' : 'mdi-chevron-down'
    }
  },
  methods: {
    async getRepairFileList() {
      this.loading = true
      try {
        const response = await RepairFileService.getPaginatedRepairFileList(
          this.sort,
          this.order,
          this.page,
          this.limit
        )
        this.repairFileList = response.data
        this.maxPage = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    redirectToRepairFileFullList() {
      this.$router.push('/dashboard/repair-file')
    },
    async loadMoreRepairFile() {
      if (this.loadingMore || this.loading || this.page >= this.maxPage) {
        return
      }

      this.loadingMore = true
      this.page = this.page + 1

      try {
        const response = await RepairFileService.getPaginatedRepairFileList(
          this.sort,
          this.order,
          this.page,
          this.limit
        )

        this.repairFileList = [...this.repairFileList, ...response.data]
        this.maxPage = response.max_page
      } catch (error) {
        handleResponseError(error)
        this.page = this.page - 1
      } finally {
        this.loadingMore = false
      }
    },
    setupInfiniteScroll() {
      this.$nextTick(() => {
        const container = this.$refs.dataTable?.$el?.querySelector('.v-table__wrapper')
        if (container) {
          this.scrollContainer = container
          this.handleScroll = this.onScroll.bind(this)
          container.addEventListener('scroll', this.handleScroll)
        }
      })
    },
    cleanupInfiniteScroll() {
      if (this.scrollContainer && this.handleScroll) {
        this.scrollContainer.removeEventListener('scroll', this.handleScroll)
      }
    },
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target

      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        !this.loadingMore &&
        !this.loading &&
        this.page < this.maxPage
      ) {
        this.loadMoreRepairFile()
      }
    },
    onRowClick(_, rowEvent) {
      const item = rowEvent.item
      if (item.status_detail !== RepairFileStatusDetailConstant.DELETED) {
        router.push(`/repair-file-detail/${item.boomerang_id}`)
      }
    },
    sortBy(item) {
      this.page = 1
      if (item.length > 0) {
        this.isOrderNeutral = false
        this.sort = item[0].key
        this.order = item[0].order === 'asc' ? 'desc' : 'asc'
      } else {
        this.isOrderNeutral = true
        this.order = 'desc'
      }
      this.getRepairFileList()
    },
    getInteractionUserColor(repairFile) {
      if (
        repairFile.status_detail === RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER ||
        repairFile.status_detail === RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER ||
        repairFile.status_detail === RepairFileStatusDetailConstant.RECEIPT_AT_STORE ||
        repairFile.status_detail === RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER
      ) {
        return 'text-orange'
      }
    },
    getDataTableFormattedValue(value) {
      return value ? value : this.$t('COMMON.NO_VALUE')
    },
    getStatusMacro(status_macro) {
      return this.$t('COMMON.STATUS_MACRO.' + status_macro)
    },
    setDayStyle(daysSinceCreation) {
      if (daysSinceCreation >= 20) {
        return 'red'
      } else if (daysSinceCreation >= 10) {
        return 'orange'
      } else {
        return 'green'
      }
    },
    getTransporterModality(modality) {
      if (modality in TransportModalityConstant) {
        return this.$t('REPAIR_FILE_LIST_PAGE.MODALITY.' + modality)
      } else {
        return this.$t('COMMON.NO_VALUE')
      }
    }
  }
}
</script>

<template>
  <v-card class="rounded-xl current-repair-files" :border="false" :elevation="0">
    <div class="d-flex justify-space-between align-center">
      <h3>{{ $t('CURRENT_REPAIR_FILES.TITLE') }}</h3>
      <div class="d-flex fullscreen" @click="redirectToRepairFileFullList()">
        <img alt="fullscreen" class="mr-2" src="@/assets/icons/dashboard/fullscreen.svg" />
        <span class="underline">{{ $t('CURRENT_REPAIR_FILES.SEE_MORE') }}</span>
      </div>
    </div>

    <div class="table-wrapper">
      <v-data-table
        class="mt-4"
        ref="dataTable"
        :loading="loading"
        :headers="headers"
        :items="repairFileList"
        @click:row="onRowClick"
        :sort-asc-icon="ascSortIcon"
        :sort-desc-icon="descSortIcon"
        @update:sort-by="sortBy"
        height="520"
      >
        <template #loading>
          <div class="d-flex flex-column justify-center align-center pa-8">
            {{ $t('REPAIR_FILE_LIST_PAGE.TABLE.LOADING_REPAIR_FILES') }}
            <v-progress-linear class="my-5" color="#165039" indeterminate />
          </div>
        </template>

        <template #no-data>
          <div class="d-flex flex-column justify-center align-center pa-8">
            {{ $t('REPAIR_FILE_LIST_PAGE.TABLE.NO_REPAIR_FILE') }}
            <div class="d-flex align-center justify-center mt-8">
              <img src="@/assets/images/empty.svg" width="100" alt="Empty" />
            </div>
          </div>
        </template>

        <template #[`item.internal_command_number`]="{ item }">
          <span class="color-light" :class="getInteractionUserColor(item)">
            {{ item.internal_command_number }}
          </span>
        </template>

        <template #[`item.company`]="{ item }">
          <span class="font-weight-bold">
            {{ getDataTableFormattedValue(item.company) }}
          </span>
        </template>

        <template #[`item.manufacturer_name`]="{ item }">
          <span class="font-weight-bold">
            {{ item.manufacturer_name }}
          </span>
        </template>

        <template #[`item.product_name`]="{ item }">
          <span class="color-light">
            {{ item.product_name }}
          </span>
        </template>

        <template #[`item.status_macro`]="{ item }">
          <span>{{ getStatusMacro(item.status_macro) }}</span>
        </template>

        <template #[`item.business_created_day_count`]="{ item }">
          <span :class="setDayStyle(item.business_created_day_count)">
            {{
              item.business_created_day_count >= 0
                ? item.business_created_day_count + ' ' + $t('REPAIR_FILE_LIST_PAGE.DAYS')
                : $t('COMMON.NO_VALUE')
            }}
          </span>
        </template>

        <template #[`item.transporter_modality`]="{ item }">
          <span>
            {{ getTransporterModality(item.transporter_modality) }}
          </span>
        </template>

        <template #bottom>
          <div v-if="loadingMore" class="d-flex justify-center align-center pa-4">
            <v-progress-circular v-if="loadingMore" indeterminate color="#165039" size="24" />
            <span v-if="loadingMore" class="ml-3">
              {{ $t('REPAIR_FILE_LIST_PAGE.TABLE.LOADING_REPAIR_FILES') }}
            </span>
          </div>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<style scoped>
.current-repair-files {
  display: flex;
  height: 630px;
  align-self: stretch;
  flex-direction: column;
  padding: 20px 24px !important;
}

h3 {
  font-size: 18px;
  font-weight: 900;
  line-height: normal;
}

.fullscreen,
.download {
  cursor: pointer;
}

.underline {
  font-size: 14px;
  line-height: normal;
  text-decoration: underline;
}

.color-light {
  color: #bfbfbf;
}

.red {
  background-color: #ff5c5c;
}

.orange {
  background-color: var(--warning-color);
}

.green {
  background-color: #86c94e;
}

.text-orange {
  color: var(--warning-color);
}

.red,
.orange,
.green {
  color: white;
  display: flex;
  padding: 5px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 5px;
}
</style>
